import React, { useState } from "react";
import { Instagram, Facebook, Youtube, Linkedin, Sparkles, RefreshCcw, Check, Loader2, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";
import Stepper from "@/components/Stepper";
import { PageContainer, PageHeader } from "@/components/Page";
import api from "@/lib/api";

const platforms = [
  { id: "instagram", name: "Instagram", icon: Instagram, color: "#E4405F", defaultAspect: "1:1" },
  { id: "facebook", name: "Facebook", icon: Facebook, color: "#1877F2", defaultAspect: "1:1" },
  { id: "tiktok", name: "TikTok", icon: () => <span className="text-2xl font-bold">♪</span>, color: "#000", defaultAspect: "9:16" },
  { id: "youtube-shorts", name: "YouTube Shorts", icon: Youtube, color: "#FF0000", defaultAspect: "9:16" },
  { id: "linkedin", name: "LinkedIn", icon: Linkedin, color: "#0A66C2", defaultAspect: "1:1" },
];
const tones = ["Engaging", "Playful", "Professional", "Bold", "Inspirational", "Educational"];
const aspects = ["1:1", "4:5", "9:16", "16:9"];

const aspectClass = {
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "9:16": "aspect-[9/16]",
  "16:9": "aspect-video",
};

const ContentStudio = () => {
  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState("instagram");
  const [aspect, setAspect] = useState("1:1");
  const [tone, setTone] = useState("Engaging");
  const [topic, setTopic] = useState("");
  const [goal, setGoal] = useState("");

  const [captions, setCaptions] = useState([]);
  const [selectedCaption, setSelectedCaption] = useState(null);
  const [editedCaption, setEditedCaption] = useState("");
  const [loadingCaptions, setLoadingCaptions] = useState(false);

  const [imageUrl, setImageUrl] = useState(null);
  const [imageId, setImageId] = useState(null);
  const [imagePrompt, setImagePrompt] = useState("");
  const [loadingImage, setLoadingImage] = useState(false);

  const [scheduleDate, setScheduleDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedPostId, setSavedPostId] = useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onPickPlatform = (p) => {
    setPlatform(p.id);
    setAspect(p.defaultAspect);
  };

  const formatCaption = (c) =>
    `${c.hook || ""}\n\n${c.body || ""}\n\n${c.cta || ""}\n\n${(c.hashtags || []).join(" ")}`.trim();

  const generateCaptions = async () => {
    if (!topic.trim()) return toast.error("Please add a topic first");
    setLoadingCaptions(true);
    try {
      const { data } = await api.post("/social/generate-captions", {
        topic, platform, tone: tone.toLowerCase(), goal,
      });
      setCaptions(data.captions || []);
      if ((data.captions || []).length > 0) {
        setSelectedCaption(0);
        setEditedCaption(formatCaption(data.captions[0]));
      }
      next();
    } catch (e) {
      toast.error("Caption generation failed. Try again.");
    } finally {
      setLoadingCaptions(false);
    }
  };

  const generateImage = async () => {
    const prompt = imagePrompt.trim() || topic.trim();
    if (!prompt) return toast.error("Add an image prompt first");
    setLoadingImage(true);
    try {
      const { data } = await api.post("/social/generate-image", {
        prompt, aspect_ratio: aspect, platform,
      });
      setImageUrl(`${backendBase}${data.image_url}`);
      setImageId(data.id);
      toast.success("Image ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image generation failed");
    } finally {
      setLoadingImage(false);
    }
  };

  const savePost = async (mode) => {
    if (!editedCaption.trim()) return toast.error("Caption is empty");
    setSaving(true);
    try {
      const sel = captions[selectedCaption] || {};
      const body = {
        platform,
        caption: editedCaption,
        hashtags: sel.hashtags || [],
        image_url: imageUrl ? imageUrl.replace(backendBase, "") : null,
        image_id: imageId,
        aspect_ratio: aspect,
        topic,
      };
      if (mode === "schedule") {
        if (!scheduleDate) return toast.error("Pick a schedule date");
        body.schedule_date = new Date(scheduleDate).toISOString();
      }
      const { data } = await api.post("/social/posts", body);
      setSavedPostId(data.id);
      toast.success(mode === "schedule" ? "Scheduled to Calendar" : "Saved as draft");
      next();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setStep(0); setTopic(""); setGoal(""); setCaptions([]); setSelectedCaption(null); setEditedCaption("");
    setImageUrl(null); setImageId(null); setImagePrompt(""); setScheduleDate(""); setSavedPostId(null);
  };

  return (
    <PageContainer>
      <PageHeader title="AI Content Studio" subtitle="Generate platform-perfect posts in 4 steps." testId="content-studio-header" />
      <Stepper steps={["Configure", "Caption", "Visual", "Review"]} current={step} />

      {/* STEP 0 — CONFIGURE */}
      {step === 0 && (
        <div className="max-w-3xl mx-auto" data-testid="cs-step-configure">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Pick a platform</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
            {platforms.map((p) => {
              const Icon = p.icon;
              const active = platform === p.id;
              return (
                <button key={p.id} onClick={() => onPickPlatform(p)}
                  className={`op-card p-4 text-center transition ${active ? "border-[#7C3AED] ring-2 ring-purple-100" : ""}`}
                  data-testid={`cs-platform-${p.id}`}>
                  <div className="mb-2 flex justify-center"><Icon style={{ color: p.color }} size={28} /></div>
                  <p className="text-sm font-semibold text-gray-900">{p.name}</p>
                </button>
              );
            })}
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-2">Aspect ratio</h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {aspects.map((r) => (
              <button key={r} onClick={() => setAspect(r)}
                className={aspect === r ? "op-pill-active" : "op-pill-inactive"}
                data-testid={`cs-aspect-${r.replace(":","x")}`}>{r}</button>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-2">Tone</h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {tones.map((t) => (
              <button key={t} onClick={() => setTone(t)}
                className={tone === t ? "op-pill-active" : "op-pill-inactive"}
                data-testid={`cs-tone-${t.toLowerCase()}`}>{t}</button>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-2">Topic</h3>
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Weekend brunch promo with avocado toast"
            className="op-input mb-4" data-testid="cs-topic" />

          <h3 className="text-sm font-semibold text-gray-700 mb-2">Goal (optional)</h3>
          <input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. drive Saturday foot traffic"
            className="op-input" data-testid="cs-goal" />
        </div>
      )}

      {/* STEP 1 — CAPTION */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto" data-testid="cs-step-caption">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Pick a caption</h2>
            <button onClick={generateCaptions} disabled={loadingCaptions} className="op-btn-secondary flex items-center gap-2" data-testid="cs-regen-captions">
              {loadingCaptions ? <Loader2 size={16} className="animate-spin" /> : <RefreshCcw size={16} />} Regenerate
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            {captions.map((c, i) => (
              <button key={i} onClick={() => { setSelectedCaption(i); setEditedCaption(formatCaption(c)); }}
                className={`op-card p-4 text-left transition ${selectedCaption === i ? "border-[#7C3AED] ring-2 ring-purple-100" : ""}`}
                data-testid={`cs-caption-${i}`}>
                <p className="font-semibold text-gray-900 mb-2 text-sm leading-snug">{c.hook}</p>
                <p className="text-xs text-gray-600 leading-relaxed line-clamp-4">{c.body}</p>
                <p className="text-xs text-[#7C3AED] mt-2 font-medium">{c.cta}</p>
                <div className="text-[11px] text-gray-400 mt-2 truncate">{(c.hashtags || []).join(" ")}</div>
              </button>
            ))}
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Edit caption</h3>
          <textarea value={editedCaption} onChange={(e) => setEditedCaption(e.target.value)}
            rows={8} className="op-input font-mono text-sm" data-testid="cs-caption-edit" />
        </div>
      )}

      {/* STEP 2 — VISUAL */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto" data-testid="cs-step-visual">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate the visual</h2>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Image prompt</h3>
          <textarea value={imagePrompt} onChange={(e) => setImagePrompt(e.target.value)} rows={3}
            placeholder={`e.g. ${topic || "avocado toast on rustic wooden plate, golden morning light"}`}
            className="op-input mb-3" data-testid="cs-image-prompt" />
          <div className="flex items-center gap-3 mb-6">
            <button onClick={generateImage} disabled={loadingImage}
              className="op-btn-primary flex items-center gap-2" data-testid="cs-generate-image">
              {loadingImage ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {imageUrl ? "Regenerate image" : "Generate image"}
            </button>
            <span className="text-xs text-gray-500">Powered by Gemini Nano Banana · {aspect}</span>
          </div>
          <div className={`relative mx-auto bg-gray-100 rounded-xl overflow-hidden border border-gray-200 ${aspectClass[aspect]} max-w-md`}>
            {loadingImage && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-10">
                <div className="flex flex-col items-center gap-2 text-gray-500">
                  <Loader2 size={28} className="animate-spin" />
                  <p className="text-xs">Generating… ~10s</p>
                </div>
              </div>
            )}
            {imageUrl ? (
              <img src={imageUrl} alt="generated" className="w-full h-full object-cover" data-testid="cs-image-preview" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No image yet</div>
            )}
          </div>
        </div>
      )}

      {/* STEP 3 — REVIEW */}
      {step === 3 && !savedPostId && (
        <div className="max-w-4xl mx-auto" data-testid="cs-step-review">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Review & publish</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Preview</p>
              <div className="op-card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500" />
                  <p className="text-sm font-semibold">your_brand</p>
                  <span className="ml-auto text-xs text-gray-400 capitalize">{platform}</span>
                </div>
                {imageUrl && (
                  <div className={`bg-gray-100 rounded-lg overflow-hidden mb-3 ${aspectClass[aspect]}`}>
                    <img src={imageUrl} alt="post" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-sm whitespace-pre-line text-gray-800">{editedCaption}</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Publish options</p>
              <div className="op-card p-5 space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">Schedule date & time</label>
                  <input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)}
                    className="op-input" data-testid="cs-schedule-date" />
                  <p className="text-xs text-gray-400 mt-1">Adds to your Content Calendar</p>
                </div>
                <button onClick={() => savePost("schedule")} disabled={saving || !scheduleDate}
                  className="op-btn-primary w-full flex items-center justify-center gap-2" data-testid="cs-schedule-btn">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <CalIcon size={16} />} Schedule to Calendar
                </button>
                <button onClick={() => savePost("draft")} disabled={saving}
                  className="op-btn-secondary w-full" data-testid="cs-save-draft-btn">
                  Save as draft
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {step === 3 && savedPostId && (
        <div className="text-center py-10" data-testid="cs-step-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Check size={28} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Post saved!</h2>
          <p className="text-gray-500 mb-6">Find it in your Content Calendar or social drafts.</p>
          <button onClick={reset} className="op-btn-primary" data-testid="cs-create-another">Create another</button>
        </div>
      )}

      {step < 3 && (
        <div className="flex justify-center gap-2 mt-10">
          {step > 0 && <button onClick={back} className="op-btn-secondary" data-testid="cs-back">Back</button>}
          {step === 0 && (
            <button onClick={generateCaptions} disabled={!topic.trim() || loadingCaptions}
              className="op-btn-primary flex items-center gap-2" data-testid="cs-next-captions">
              {loadingCaptions ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate captions
            </button>
          )}
          {step === 1 && (
            <button onClick={next} disabled={selectedCaption === null} className="op-btn-primary" data-testid="cs-next-visual">
              Next: Visual →
            </button>
          )}
          {step === 2 && (
            <button onClick={next} disabled={!imageUrl} className="op-btn-primary" data-testid="cs-next-review">
              Next: Review →
            </button>
          )}
        </div>
      )}
    </PageContainer>
  );
};
export default ContentStudio;
